/* eslint-disable no-await-in-loop */
import { restApiRequest } from "src/utils/Api";
import { dynamicNotification } from "src/utils/Notifications";
import __ from "src/utils/Translations";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";
import { DEFAULT_ITEMS_COUNT_PER_REQUEST } from "src/utils/constants/api";
import modifyResponse from "./modifyResponse";

const FIELDS_FOR_BATCHING = ["id"];

const sendBatchRequests = async ({
  skipAction = false,
  filterField,
  filterValue,
  idAsIri,
  asOption,
  benefitSubgroupId,
  IRI_PREFIX,
  requestParams,
  mappingType,
  isTable = false,
  setData = () => {},
  customModifyResponse = null,
  customModifyAllResponses = null,
}) => {
  if (skipAction) {
    setData([]);
    return null;
  }

  const {
    service,
    path,
    method,
    mockData,
    itemsPerPage = DEFAULT_ITEMS_COUNT_PER_REQUEST,
    startPage,
    additionalParams = {},
  } = requestParams;

  // send requests in parallel
  if (
    !!itemsPerPage &&
    FIELDS_FOR_BATCHING.includes(filterField) &&
    filterValue?.length
  ) {
    // Batch the IDs and make multiple requests
    const batches = Array.from(
      { length: Math.ceil(filterValue?.length / itemsPerPage) },
      (_, i) => filterValue?.slice(i * itemsPerPage, (i + 1) * itemsPerPage)
    );

    try {
      const responses = await Promise.all(
        batches.map((batch) =>
          restApiRequest(
            service,
            path,
            method,
            {
              params: {
                ...additionalParams,
                [filterField]: batch,
                itemsPerPage,
              },
            },
            mockData,
            isTable
          )
        )
      );

      if (customModifyResponse) {
        let result = customModifyResponse(responses.flat());
        if (customModifyAllResponses) {
          result = customModifyAllResponses(result);
        }
        return result;
      }

      const result = modifyResponse({
        resData: responses.flat(),
        idAsIri,
        asOption,
        benefitSubgroupId,
        IRI_PREFIX,
        mappingType,
      });

      setData(result);

      return result;
    } catch (e) {
      newRelicErrorReport(
        e,
        "utils/hooks/company/utils/sendBatchRequests.js - 82"
      );
      dynamicNotification(
        e.message || __("Nie udało się pobrać danych"),
        "error"
      );
      return null;
    }
  } else {
    // send requests one after another
    try {
      let page = startPage ?? 1;
      const responses = [];
      let hasMore = true;

      while (hasMore) {
        const resData =
          (await restApiRequest(
            service,
            path,
            method,
            {
              params: {
                ...additionalParams,
                itemsPerPage,
                ...(filterField && filterValue
                  ? { [filterField]: filterValue }
                  : {}),
                page,
              },
            },
            mockData,
            isTable
          )) || [];

        const result = customModifyResponse
          ? customModifyResponse(resData.flat())
          : modifyResponse({
              resData,
              idAsIri,
              asOption,
              benefitSubgroupId,
              IRI_PREFIX,
              mappingType,
            });

        responses.push(...result);
        hasMore = resData.length === itemsPerPage;
        page += 1;
      }

      if (customModifyAllResponses) {
        return customModifyAllResponses(responses);
      }

      setData(responses);
      return responses;
    } catch (e) {
      newRelicErrorReport(
        e,
        "utils/hooks/company/utils/sendBatchRequests.js - 134"
      );
      dynamicNotification(
        e.message || __("Nie udało się pobrać danych"),
        "error"
      );
      return null;
    }
  }
};

export default sendBatchRequests;
